import { MeResponse, type LiveEvent } from '@rp/contracts'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { subscribeLiveEvents, type LiveEventSourceFactory } from '../../api/events'
import { CONSOLE_ENV } from '../../env'
import { useAnswerTenantQueries } from '../../pwa/tenantChannel'
import { useReadySession } from '../../session/useSession'
import { createLiveInvalidator } from './invalidation'

export interface UseLiveEventsOptions {
  onInbox: (event: LiveEvent) => void
  factory?: LiveEventSourceFactory
}

export interface LiveConnectionState {
  connected: boolean
}

const ME_PATH = '/v1/me'

const keepRetrying = (): void => undefined

export const useLiveEvents = ({ onInbox, factory }: UseLiveEventsOptions): LiveConnectionState => {
  const { api, membership } = useReadySession()
  const [connected, setConnected] = useState(true)
  const queryClient = useQueryClient()
  const onInboxRef = useRef(onInbox)
  const tenantId = membership.tenantId
  const carrying = useRef<string | null>(null)
  useAnswerTenantQueries(carrying)

  useEffect(() => {
    onInboxRef.current = onInbox
  }, [onInbox])

  useEffect(() => {
    const invalidator = createLiveInvalidator({ queryClient, tenantId })
    let readyBefore = false
    const stop = subscribeLiveEvents({
      baseUrl: CONSOLE_ENV.apiBaseUrl,
      tenantId,
      ...(factory === undefined ? {} : { factory }),
      onReady: () => {
        carrying.current = tenantId
        if (readyBefore) invalidator.resync()
        readyBefore = true
      },
      onClosed: () => {
        carrying.current = null
      },
      onConnection: setConnected,
      onRefused: () => {
        api.request(MeResponse, { method: 'GET', path: ME_PATH }).catch(keepRetrying)
      },
      onEvent: (event) => {
        invalidator.note(event.conversationId)
        if (event.kind === 'inbox') onInboxRef.current(event)
      },
    })
    return () => {
      stop()
      invalidator.cancel()
      carrying.current = null
      setConnected(true)
    }
  }, [api, factory, queryClient, tenantId])

  return { connected }
}
